import React, {useEffect, useState} from 'react';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Typography,
  makeStyles,
} from '@material-ui/core';

import {colors} from '../../../../assets/styles/colors';
import ArrowLeftIcon from '../../../../elements/icons/ArrowLeftIcon';
import XmarkIcon from '../../../../elements/icons/XmarkIcon';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import type {Category} from '../../../../entities';
import {useFetchProducts, useProducts, useProductPagination} from '../../utils';
import {
  useSelectedProductActions,
  useSelectedProducts,
  useSelectedProductsVariants,
  useToggleProductSelection,
} from '../../utils/bulkEditOptions';
import {
  extractInfoProduct,
  updateDataProductsNewCategories,
} from '../../utils/extractInfoProduct';
import {useAlert} from '../../../../elements/Alert/AlertContext';
import {ProductBulkEdit} from '../../../../entities';
import {customErrorBulkMessage} from '../../utils/customErrorMessage';

const useStyles = makeStyles(theme => ({
  infoText: {
    color: colors.grey700,
    fontSize: '.85rem',
    maxWidth: '100%',

    [theme.breakpoints.up('md')]: {
      maxWidth: '40ch',
    },

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  scrollArrow: {
    padding: '.75rem',
  },
  icon: {
    width: '2.2rem',
    height: '2.2rem',
  },
  productQuantitySelected: {
    fontWeight: 600,
    width: '230px',
  },
  productQuantitySelectedSpan: {
    color: colors.grey700,
    fontSize: '.85rem',
  },
  flexCenterColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '0.1rem',
  },
  iconButtonBack: {
    background: 'white',
  },
  iconButton: {
    background: 'white',

    '&:hover': {
      background: colors.purple1,
    },
  },
  iconButtonActive: {
    background: colors.purple1,
    color: colors.purple3,
    fontWeight: 600,
  },
  iconButtonMobile: {
    background: 'white',
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  textFieldCustom: {
    width: '10rem',

    '& input': {
      textAlign: 'right',
    },

    [theme.breakpoints.down('md')]: {
      minWidth: '8rem',
      width: '100%',
    },
  },
  ofertGroupButtonInput: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
    rowGap: '1rem',
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      width: 'fit-content',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  ofertGroupButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
  },
  contentBack: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      width: 'fit-content',
    },
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'space-between',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  button: {
    width: '300px',
    backgroundColor: colors.success,
    color: '#fff',
    fontSize: '1rem',

    '&:hover': {
      backgroundColor: '#01815f',
    },

    [theme.breakpoints.down('sm')]: {
      width: '50%',
      margin: '0 auto',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  containerOption: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    overflowX: 'auto',
    gap: '7%',

    [theme.breakpoints.down('md')]: {
      overflowX: 'hidden',
      flexDirection: 'column',
      rowGap: '2rem',
    },

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  spaceBetwen: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  groupButtonApply: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'fit-content',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  iconButtonDesktopOption: {
    display: 'none',
    background: 'white',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
  sizeSelect: {
    width: '15rem',

    [theme.breakpoints.down('sm')]: {
      width: '24rem',
    },
  },
}));

type showOptionBulk = 'NONE' | 'ALL' | 'CATEGORY' | 'OFERT' | 'STOCK' | 'PRICE';

type Props = {
  categories: Category[],
  productCount: number,
  variantCount: number,
  setShowBulkOption: (value: showOptionBulk) => void,
  removeAllToggleProductSelection: () => void,
};

export default function BulkEditNewCategory({
  categories,
  productCount,
  variantCount,
  setShowBulkOption,
  removeAllToggleProductSelection,
}: Props) {
  const classes = useStyles();

  const [valueNewCategory, setValueNewCategory] = useState(0);
  const {getProducts} = useFetchProducts();

  const toggleProductSelected = useToggleProductSelection();
  const {products, isFetchingProducts} = useProducts();
  const pagination = useProductPagination();
  const productSelection = useSelectedProducts();
  const productsVariantsSelection = useSelectedProductsVariants();
  const {editSelectedProducts} = useSelectedProductActions();
  const {showAlert} = useAlert();

  const editDataProducts = (data: ProductBulkEdit[]) => {
    editSelectedProducts(data)
      .then(() => {
        showAlert('Productos actualizados', {type: 'success'});
        getProducts({
          page: pagination.page,
          size: pagination.size,
        });
      })
      .catch(e => {
        const errorMessage = customErrorBulkMessage(e.error);
        showAlert(errorMessage, {type: 'error'});
      });
  };

  const updateProductsNewCategory = () => {
    const {productsInfo} = extractInfoProduct(
      productSelection,
      productsVariantsSelection
    );

    const updatedProducts = updateDataProductsNewCategories(
      productsInfo,
      productSelection,
      valueNewCategory
    );

    editDataProducts(updatedProducts);
  };

  useEffect(() => {
    const selectedProductsSet = new Set(productSelection);

    products.forEach(product => {
      const variants = product?.productVariantSetting?.variants;
      if (!variants) return;
      variants.forEach(variant => {
        if (productsVariantsSelection.some(pvs => pvs.id === variant.id)) {
          selectedProductsSet.add(product);
        }
      });
    });

    selectedProductsSet.forEach(product => {
      if (productSelection.some(prs => prs.id === product.id)) return;
      toggleProductSelected(product);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsVariantsSelection, setShowBulkOption]);

  useEffect(() => {
    if (productSelection.length === 0) {
      setShowBulkOption('ALL');
    }
  }, [productSelection, setShowBulkOption]);

  return (
    <div className={classes.containerOption}>
      <div className={classes.contentBack}>
        <div className={classes.contentBack}>
          <IconButton
            className={classes.iconButtonBack}
            onClick={() => {
              setShowBulkOption('ALL');
            }}
          >
            <ArrowLeftIcon />
          </IconButton>

          <div className={classes.flexCenterColumn}>
            <Typography className={classes.productQuantitySelected}>
              Categoría nueva
            </Typography>
            <Typography
              component={'span'}
              className={classes.productQuantitySelectedSpan}
            >
              Producto{productCount > 1 ? 's' : ''} seleccionado
              {productCount > 1 ? 's' : ''}: {productCount}
            </Typography>
            <Typography
              component={'span'}
              className={classes.productQuantitySelectedSpan}
            >
              Variante{variantCount > 1 ? 's' : ''} seleccionado
              {variantCount > 1 ? 's' : ''}: {variantCount}
            </Typography>
          </div>
        </div>
        <IconButton
          className={classes.iconButtonMobile}
          onClick={() => {
            removeAllToggleProductSelection();
            setShowBulkOption('ALL');
          }}
        >
          <XmarkIcon />
        </IconButton>
      </div>
      <div className={classes.content}>
        <div className={classes.row}>
          <div className={classes.ofertGroupButtonInput}>
            <CustomSelect
              pagination
              disableUnderline
              className={classes.sizeSelect}
              defaultValue={0}
              value={valueNewCategory}
              onChange={event => setValueNewCategory(event.target.value)}
            >
              <MenuItem value={0} disabled>
                Seleccionar categoría
              </MenuItem>
              {categories.map(category => (
                <MenuItem
                  key={category.id}
                  value={category.id}
                  disabled={!category.enabled}
                >
                  {category.name}
                </MenuItem>
              ))}
            </CustomSelect>
          </div>

          <Typography className={classes.infoText}>
            Se modificará la categoría para todos los productos seleccionados.
          </Typography>
        </div>

        <div className={classes.groupButtonApply}>
          {isFetchingProducts ? (
            <CircularProgress />
          ) : (
            <CustomButton
              type="button"
              className={classes.button}
              onClick={updateProductsNewCategory}
            >
              Aplicar
            </CustomButton>
          )}
          <IconButton
            className={classes.iconButtonDesktopOption}
            onClick={() => {
              removeAllToggleProductSelection();
              setShowBulkOption('ALL');
            }}
          >
            <XmarkIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
